import { z } from "zod";

const eventTypes = ["match_day", "practice", "tryout", "tournament"] as const;

export const createEventSchema = z
  .object({
    sport_id: z.string().min(1).nullish(),
    name: z.string().min(1, "Name is required").max(255),
    event_type: z.enum(eventTypes, { message: "Invalid event type" }),
    starts_at: z.string().min(1, "Start time is required"),
    ends_at: z.string().min(1, "End time is required"),
    // 150 — trials target an age group with no teams ("no teams, just age group")
    age_group_id: z.string().min(1).nullish(),
  })
  .refine((data) => new Date(data.ends_at) > new Date(data.starts_at), {
    message: "End time must be after start time",
    path: ["ends_at"],
  });

export const updateEventSchema = z.object({
  sport_id: z.string().min(1).nullish(),
  name: z.string().min(1, "Name is required").max(255).optional(),
  event_type: z
    .enum(eventTypes, { message: "Invalid event type" })
    .optional(),
  starts_at: z.string().min(1).optional(),
  ends_at: z.string().min(1).optional(),
  age_group_id: z.string().min(1).nullish(), // 150 — trials targeting
});

export const addEventSchoolSchema = z.object({
  school_id: z.string().min(1, "School is required"),
  is_host: z.boolean().optional(),
});

export const addEventCourtSchema = z
  .object({
    court_id: z.string().min(1).optional(),
    name: z.string().min(1).max(100).optional(),
    event_venue_id: z.string().min(1).optional(),
  })
  .refine((data) => Boolean(data.court_id) || Boolean(data.name), {
    message: "Either court_id or name is required",
  });

export const updateEventSchoolSchema = z.object({
  is_host: z.boolean().optional(),
  team_names: z.array(z.string().min(1)).optional(),
});

// 080 -- lifecycle action payloads
export const publishEventSchema = z.object({
  notify: z.boolean().optional(), // default true server-side
  // 191 -- set after the user confirms the schedule-conflict warning; without
  // it, publish returns 409 { code: "schedule_conflicts", conflicts } when
  // same-field overlaps exist. Warn-and-confirm, never a hard block.
  acknowledge_conflicts: z.boolean().optional(),
});

export const cancelEventSchema = z.object({
  reason: z.string().min(1, "Reason is required").max(1000),
  notify: z.boolean().optional(), // default true server-side
});

// Grace-window unpublish takes no body.
export const unpublishEventSchema = z.object({}).optional();

export type CreateEventInput = z.infer<typeof createEventSchema>;
export type UpdateEventInput = z.infer<typeof updateEventSchema>;
export type AddEventSchoolInput = z.infer<typeof addEventSchoolSchema>;
export type AddEventCourtInput = z.infer<typeof addEventCourtSchema>;
export type PublishEventInput = z.infer<typeof publishEventSchema>;
export type CancelEventInput = z.infer<typeof cancelEventSchema>;
